import React, { createContext, ReactNode, useContext } from 'react';

import { ServerAnalytics } from '@/analytics/server-analytics';
import { ServerGTMScript } from '@/analytics/server-gtm-script';
import { LoyaltyPageData } from '@/utils/ssr';

import { UserContextData } from './types';

export type AnalyticsProps = {
  userContext: UserContextData;
  environment: string;
  market: string;
  pageData?: LoyaltyPageData;
  telemedicineEligible?: boolean;
};

export type AnalyticsProviderProps = AnalyticsProps & {
  children?: ReactNode;
  pageData?: Record<string, any>;
};

export type GAEventData = {
  category: string;
  action: string;
  label: string;
  value?: number;
  extraData?: object;
};

export type SegmentEventData = {
  eventCategory: string;
  eventAction: string;
  eventLabel: string;
  extraData?: object;
};

export const PAGE_TYPE = 'membership';
// TODO: LOYAL-1572 env vars are not accessible

type StorefrontAnalytics = {
  trackEvent: (
    category: string,
    action: string,
    label: string,
    value?: number,
    extraData?: object,
  ) => void;
  segmentTrack: (eventName: string, eventData: SegmentEventData, isInteractive: boolean) => void;
};

type AnalyticsAction = (analytics: StorefrontAnalytics) => void;

let storefrontAnalytics: StorefrontAnalytics | null = null;
let storefrontAnalyticsPromise: Promise<StorefrontAnalytics | null> | null = null;
let queuedAnalyticsActions: AnalyticsAction[] = [];

const loadStorefrontAnalytics = async () => {
  if (storefrontAnalytics) return storefrontAnalytics;

  if (!storefrontAnalyticsPromise) {
    storefrontAnalyticsPromise = import('storefront-analytics/dist/analytics')
      .then(({ default: analytics }) => {
        storefrontAnalytics = analytics;
        return storefrontAnalytics;
      })
      .catch(error => {
        storefrontAnalyticsPromise = null;
        // Analytics should never take down the page if an async chunk is stale or unavailable.
        // eslint-disable-next-line no-console
        console.warn('Failed to load storefront analytics chunk', error);
        return null;
      });
  }

  return storefrontAnalyticsPromise;
};

const flushQueuedAnalyticsActions = (analytics: StorefrontAnalytics) => {
  const actionsToFlush = queuedAnalyticsActions;
  queuedAnalyticsActions = [];
  actionsToFlush.forEach(action => action(analytics));
};

const queueAnalyticsAction = async (action: AnalyticsAction) => {
  if (storefrontAnalytics) {
    action(storefrontAnalytics);
    return;
  }

  queuedAnalyticsActions.push(action);
  const analytics = await loadStorefrontAnalytics();
  if (!analytics) {
    queuedAnalyticsActions = [];
    return;
  }

  flushQueuedAnalyticsActions(analytics);
};

export const AnalyticsContext = createContext<{
  environment?: string;
  trackEvent: (obj: GAEventData, isInteractive?: boolean) => void;
  trackSegmentEvent: (eventName: string, obj: SegmentEventData) => void;
}>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  trackEvent: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  trackSegmentEvent: () => {},
});

const AnalyticsProvider = ({
  children,
  userContext,
  environment,
  market,
  pageData,
  telemedicineEligible,
}: AnalyticsProviderProps) => {
  const trackEvent = async (eventData: GAEventData, isInteractive = true) => {
    if (typeof window !== 'undefined') {
      await queueAnalyticsAction(analytics => {
        analytics.trackEvent(
          eventData.category,
          eventData.action,
          eventData.label,
          eventData.value,
          {
            ...eventData.extraData,
            eventNonInt: !isInteractive,
          },
        );
      });
    }
  };

  const trackSegmentEvent = async (eventName: string, eventData: SegmentEventData) => {
    if (typeof window !== 'undefined') {
      await queueAnalyticsAction(analytics => {
        analytics.segmentTrack(eventName, eventData, false);
      });
    }
  };

  return (
    <AnalyticsContext.Provider
      value={{
        environment,
        trackEvent,
        trackSegmentEvent,
      }}
    >
      {ServerGTMScript(market)}
      <ServerAnalytics
        environment={environment}
        market={market}
        pageData={pageData}
        telemedicineEligible={telemedicineEligible}
        userContextData={userContext}
      />
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => useContext(AnalyticsContext);
export default AnalyticsProvider;
